// =============================================================================
// TrackerCalendar.jsx
//
// Vista de calendário para as actividades do Tracker.
// Usa FullCalendar com plugins: dayGrid, timeGrid, list, interaction.
//
// Props:
//   entries      → array de entradas (filtradas)
//   featureTypes → [{ name, color }] — cores dinâmicas da BD
//   onRowClick   → callback(entry) — abre modal de edição
//   showClosed   → boolean
// =============================================================================

import React, { useMemo, useRef, useState, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin     from '@fullcalendar/daygrid';
import timeGridPlugin    from '@fullcalendar/timegrid';
import listPlugin        from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';

import {
  Paper, Box, Typography, GlobalStyles, Chip, Divider,
} from '@mui/material';
import PersonIcon   from '@mui/icons-material/Person';
import GroupsIcon   from '@mui/icons-material/Groups';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import LayersIcon   from '@mui/icons-material/Layers';
import ReplayIcon   from '@mui/icons-material/Replay';

// ── Cor por defeito ───────────────────────────────────────────────────────────
const DEFAULT_COLOR = '#9BA3AF';

// ── Paleta hardcoded (igual ao Gantt) ─────────────────────────────────────────
const FEATURE_COLOR = {
  'Feature':                    '#4E79A7',
  'Optimisation':               '#59A14F',
  'Parameter Workbook Rollout': '#E07B39',
  'Software Upgrade':           '#8B6BB1',
  'Service Protection Period':  '#E15759',
  'Maintenance':                '#e1df57',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const parseDate = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return `${slash[3]}-${slash[1]}-${slash[2]}`;
  const dash = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dash) {
    const [, y, a, b] = dash;
    return Number(a) > 12 ? `${y}-${b}-${a}` : `${y}-${a}-${b}`;
  }
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return null;
};

const fmt = (raw) => {
  const d = raw instanceof Date ? raw : (raw ? new Date(raw) : null);
  if (!d || isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
};

// FullCalendar usa end exclusivo — +1 dia para incluir o endDate
const addOneDay = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

// ── Tooltip rico (igual ao Gantt) ─────────────────────────────────────────────
const RichTooltip = ({ entry, mouseX, mouseY, featureColorMap }) => {
  const barColor  = featureColorMap[entry.feature] ?? DEFAULT_COLOR;
  const startDate = fmt(parseDate(entry.startDate));
  const endDate   = fmt(parseDate(entry.endDate));
  const phases    = (entry.phases ?? []).filter((p) => !p.label?.toLowerCase().startsWith('rollback'));
  const rollbacks = (entry.phases ?? []).filter((p) =>  p.label?.toLowerCase().startsWith('rollback'));

  const TIP_W = 280;
  const TIP_H = 320;
  const left = mouseX + 18 + TIP_W > window.innerWidth  ? mouseX - TIP_W - 12 : mouseX + 18;
  const top  = mouseY + TIP_H      > window.innerHeight ? Math.max(8, mouseY - TIP_H + 16) : mouseY - 16;

  return (
    <Box sx={{
      position: 'fixed', left, top,
      zIndex: 9999, pointerEvents: 'none', width: TIP_W,
      bgcolor: 'background.paper', borderRadius: 2,
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      border: '1px solid', borderColor: 'divider', overflow: 'hidden',
    }}>
      <Box sx={{ px: 1.75, py: 1.25, bgcolor: barColor, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'white', lineHeight: 1.3, flex: 1 }}>
          {entry.title}
        </Typography>
        {entry.technology && (
          <Chip label={entry.technology} size="small" sx={{
            height: 18, fontSize: '0.65rem', fontWeight: 800,
            bgcolor: 'rgba(255,255,255,0.25)', color: 'white', flexShrink: 0,
          }} />
        )}
      </Box>
      <Box sx={{ px: 1.75, py: 1.25 }}>
        {(entry.vendor || entry.feature) && (
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 1 }}>
            {[entry.vendor, entry.feature].filter(Boolean).join(' · ')}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
          <CalendarIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
          <Typography sx={{ fontSize: '0.74rem', color: 'text.primary' }}>
            {startDate ?? '—'}
            <Box component="span" sx={{ mx: 0.5, color: 'text.disabled' }}>→</Box>
            {endDate ?? <Box component="span" sx={{ color: 'warning.main', fontStyle: 'italic' }}>ongoing</Box>}
          </Typography>
        </Box>
        {entry.teamName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
            <GroupsIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
            <Typography sx={{ fontSize: '0.74rem' }}>{entry.teamName}</Typography>
          </Box>
        )}
        {entry.ownerNames?.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.75 }}>
            <PersonIcon sx={{ fontSize: 13, color: 'text.disabled', mt: '2px' }} />
            <Typography sx={{ fontSize: '0.74rem', lineHeight: 1.5 }}>
              {entry.ownerNames.join(', ')}
            </Typography>
          </Box>
        )}
        {phases.length > 0 && (
          <>
            <Divider sx={{ my: 0.75 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <LayersIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Phases</Typography>
            </Box>
            {phases.map((p, idx) => (
              <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3, pl: 0.5 }}>
                <Typography sx={{ fontSize: '0.72rem' }}>
                  {p.label ?? `Phase ${idx + 1}`}{p.crq ? ` — ${p.crq}` : ''}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', ml: 1, flexShrink: 0 }}>
                  {fmt(parseDate(p.date)) ?? '—'}
                </Typography>
              </Box>
            ))}
          </>
        )}
        {rollbacks.length > 0 && (
          <>
            <Divider sx={{ my: 0.75 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <ReplayIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Rollbacks</Typography>
            </Box>
            {rollbacks.map((r, idx) => (
              <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3, pl: 0.5 }}>
                <Typography sx={{ fontSize: '0.72rem' }}>
                  {r.label ?? `Rollback ${idx + 1}`}{r.crq ? ` — ${r.crq}` : ''}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', ml: 1, flexShrink: 0 }}>
                  {fmt(parseDate(r.date)) ?? '—'}
                </Typography>
              </Box>
            ))}
          </>
        )}
        {entry.comments && (
          <>
            <Divider sx={{ my: 0.75 }} />
            <Typography sx={{ fontSize: '0.71rem', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.4 }}>
              {entry.comments.length > 120 ? entry.comments.slice(0, 120) + '…' : entry.comments}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

// ── Estilos globais FullCalendar → MUI ────────────────────────────────────────
const CalendarStyles = () => (
  <GlobalStyles styles={{
    '.fc': {
      fontFamily: '"Inter","Roboto","Helvetica","Arial",sans-serif',
      fontSize: '0.82rem',
    },
    '.fc .fc-toolbar': {
      padding: '12px 16px 8px',
      flexWrap: 'wrap',
      gap: '8px',
    },
    '.fc .fc-toolbar-title': {
      fontSize: '1rem !important',
      fontWeight: '700 !important',
      color: '#374151',
    },
    '.fc .fc-button': {
      backgroundColor: 'transparent !important',
      border: '1px solid #e5e7eb !important',
      color: '#6b7280 !important',
      fontWeight: '600 !important',
      fontSize: '0.75rem !important',
      padding: '4px 12px !important',
      borderRadius: '6px !important',
      textTransform: 'capitalize !important',
      boxShadow: 'none !important',
      transition: 'all 0.15s !important',
    },
    '.fc .fc-button:hover': {
      backgroundColor: '#f3f4f6 !important',
      borderColor: '#d1d5db !important',
      color: '#374151 !important',
    },
    '.fc .fc-button-primary:not(:disabled).fc-button-active': {
      backgroundColor: '#1976d2 !important',
      borderColor: '#1976d2 !important',
      color: 'white !important',
    },
    '.fc .fc-button-group .fc-button': { borderRadius: '0 !important' },
    '.fc .fc-button-group .fc-button:first-of-type': { borderRadius: '6px 0 0 6px !important' },
    '.fc .fc-button-group .fc-button:last-of-type':  { borderRadius: '0 6px 6px 0 !important' },
    '.fc .fc-col-header-cell': { backgroundColor: '#f9fafb', padding: '6px 0' },
    '.fc .fc-col-header-cell-cushion': {
      color: '#9ca3af', fontWeight: '700', fontSize: '0.68rem',
      textTransform: 'uppercase', letterSpacing: '0.8px',
      textDecoration: 'none !important',
    },
    '.fc .fc-daygrid-day-number': {
      color: '#6b7280', fontSize: '0.78rem', fontWeight: '500',
      textDecoration: 'none !important', padding: '4px 8px',
    },
    '.fc .fc-day-today': { backgroundColor: 'rgba(25,118,210,0.04) !important' },
    '.fc .fc-day-today .fc-daygrid-day-number': {
      backgroundColor: '#1976d2', color: 'white !important',
      borderRadius: '50%', width: '26px', height: '26px',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0',
    },
    '.fc .fc-event': {
      borderRadius: '4px !important',
      border: 'none !important',
      padding: '1px 6px !important',
      fontSize: '0.72rem !important',
      fontWeight: '600 !important',
      cursor: 'pointer !important',
      transition: 'filter 0.15s',
    },
    '.fc .fc-event:hover': { filter: 'brightness(1.12)' },
    '.fc .fc-event-title': {
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    },
    '.fc-event-closed': { opacity: '0.4 !important' },
    '.fc .fc-daygrid-more-link': {
      fontSize: '0.7rem', color: '#1976d2', fontWeight: '600',
    },
    '.fc .fc-list-event': { cursor: 'pointer' },
    '.fc .fc-list-event:hover td': { backgroundColor: '#f3f4f6 !important' },
    '.fc .fc-list-day-cushion': { backgroundColor: '#f9fafb !important' },
    '.fc .fc-list-day-text, .fc .fc-list-day-side-text': {
      color: '#374151', fontWeight: '700', fontSize: '0.78rem',
      textDecoration: 'none !important',
    },
    '.fc .fc-list-event-title a': { color: '#374151 !important', textDecoration: 'none !important' },
    '.fc .fc-scrollgrid': { borderColor: '#e5e7eb !important' },
    '.fc td, .fc th': { borderColor: '#e5e7eb !important' },
    '.fc .fc-timegrid-now-indicator-line': { borderColor: '#ef4444 !important', borderWidth: '2px !important' },
    '.fc .fc-timegrid-now-indicator-arrow': { borderTopColor: '#ef4444 !important' },
    // Ocultar popover nativo — usamos o RichTooltip
    '.fc .fc-popover': { display: 'none !important' },
    '.fc-daygrid-event.fc-phase-only':  { display: 'none !important' },
    '.fc-timegrid-event.fc-phase-only': { display: 'none !important' },
    '.fc-list-event.fc-phase-only .fc-list-event-title': { paddingLeft: '16px', opacity: 0.85, fontStyle: 'italic', cursor: 'copy !important' },

  }} />
);

// ── Componente principal ──────────────────────────────────────────────────────
const TrackerCalendar = ({ entries = [], featureTypes = [], onRowClick, showClosed = false }) => {
  const calendarRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  // Mapa de cores: começa com o FEATURE_COLOR hardcoded (igual ao Gantt)
  // e sobrepõe com as cores dinâmicas da BD se existirem
  const featureColorMap = useMemo(() => {
    const map = { ...FEATURE_COLOR };
    (featureTypes ?? []).forEach((f) => { if (f.name && f.color) map[f.name] = f.color; });
    return map;
  }, [featureTypes]);

  // Converter entries → eventos FullCalendar
  const events = useMemo(() => {
    const result = [];

    entries
      .filter((e) => {
        if (!parseDate(e.startDate)) return false;
        if (!showClosed && (e.status ?? 'Open') === 'Closed') return false;
        return true;
      })
      .forEach((e) => {
        const start    = parseDate(e.startDate);
        const endRaw   = parseDate(e.endDate);
        const end      = endRaw ? addOneDay(endRaw) : addOneDay(start);
        const color    = featureColorMap[e.feature] ?? DEFAULT_COLOR;
        const isClosed = (e.status ?? 'Open') === 'Closed';

        // Dias com fase — para esconder a actividade principal nesses dias
        const phaseDays = [...new Set(
          (e.phases ?? []).map(p => parseDate(p.date)).filter(Boolean)
        )];

        // Actividade principal — todas as vistas
        result.push({
          id:              String(e.id),
          title:           e.title ?? '(sem título)',
          start,
          end,
          allDay:          true,
          backgroundColor: color,
          borderColor:     color,
          textColor:       '#ffffff',
          classNames:      isClosed ? ['fc-event-closed'] : [],
          extendedProps:   { entry: e, phaseDays },
        });

        // Um evento por fase/rollback — na Agenda substitui a actividade nesse dia
        (e.phases ?? []).forEach((phase, idx) => {
          const phaseDay = parseDate(phase.date);
          if (!phaseDay) return;
          const lbl   = phase.label ?? `Phase ${idx + 1}`;
          const title = phase.crq ? `${e.title ?? ''} — ${lbl} (${phase.crq})` : `${e.title ?? ''} — ${lbl}`;
          result.push({
            id:              `${e.id}-p${idx}`,
            title,
            start:           phaseDay,
            end:             addOneDay(phaseDay),
            allDay:          true,
            backgroundColor: color,
            borderColor:     color,
            textColor:       '#ffffff',
            classNames:      ['fc-phase-only', isClosed ? 'fc-event-closed' : ''].filter(Boolean),
            extendedProps:   { entry: e, isPhase: true },
          });
        });
      });

    return result;
  }, [entries, showClosed, featureColorMap]);

  // Clique → abre modal de edição
  const [copyMsg, setCopyMsg] = useState(null);

  const handleEventClick = useCallback(({ event }) => {
    const isPhase = event.extendedProps?.isPhase;
    if (isPhase) {
      // Encontrar CRQ no título — pode estar em qualquer posição
      const title = event.title ?? '';
      const crqMatch = title.match(/CRQ\w+/);
      const crq = crqMatch?.[0];
      if (crq) {
        navigator.clipboard.writeText(crq).then(() => {
          setCopyMsg(crq);
          setTimeout(() => setCopyMsg(null), 2500);
        }).catch(() => {
          // fallback para browsers sem permissão
          const el = document.createElement('textarea');
          el.value = crq;
          document.body.appendChild(el);
          el.select();
          document.execCommand('copy');
          document.body.removeChild(el);
          setCopyMsg(crq);
          setTimeout(() => setCopyMsg(null), 2500);
        });
      }
      return;
    }
    const entry = event.extendedProps?.entry;
    if (entry && onRowClick) onRowClick(entry);
  }, [onRowClick]);

  // Hover → RichTooltip
  const handleEventMouseEnter = useCallback(({ event, jsEvent }) => {
    setTooltip({ entry: event.extendedProps?.entry, mouseX: jsEvent.clientX, mouseY: jsEvent.clientY });
  }, []);
  const handleEventMouseLeave = useCallback(() => setTooltip(null), []);
  const handleEventMouseMove  = useCallback(({ jsEvent }) => {
    setTooltip((prev) => prev ? { ...prev, mouseX: jsEvent.clientX, mouseY: jsEvent.clientY } : prev);
  }, []);

  if (entries.length === 0) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, mt: 3 }}>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">
            No activities to display in the calendar.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <CalendarStyles />
      <Paper variant="outlined" sx={{ borderRadius: 2, mt: 2, overflow: 'hidden' }}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left:   'prev,next today',
            center: 'title',
            right:  'dayGridMonth,listMonth',
          }}
          buttonText={{
            today:     'Today',
            month:     'Month',
            listMonth: 'Agenda',
          }}
          events={events}

          eventDidMount={(info) => {
            // Agenda: esconder actividade principal nos dias com fase
            if (info.view.type === 'listMonth' && !info.event.extendedProps?.isPhase) {
              const phaseDays = info.event.extendedProps?.phaseDays ?? [];
              // O dia renderizado na agenda é a data da linha — ler do TR pai
              const rowDate = info.el.closest('tr')?.dataset?.date
                ?? info.el.closest('[data-date]')?.dataset?.date;
              // Fallback: usar a data do segmento
              const segDate = info.el.fcSeg?.start
                ? new Date(info.el.fcSeg.start).toISOString().slice(0,10)
                : null;
              const day = rowDate?.slice(0,10) ?? segDate;
              if (day && phaseDays.includes(day)) {
                info.el.closest('tr')?.style && (info.el.closest('tr').style.display = 'none');
              }
            }
            // Tooltip mousemove
            info.el.addEventListener('mousemove', (e) => {
              setTooltip((prev) => prev ? { ...prev, mouseX: e.clientX, mouseY: e.clientY } : prev);
            });

            // Click em fase/rollback — copiar CRQ directamente no DOM
            if (info.event.extendedProps?.isPhase) {
              info.el.addEventListener('click', (e) => {
                e.stopPropagation();
                const title = info.event.title ?? '';
                const crqMatch = title.match(/CRQ[A-Z0-9]+/);
                const crq = crqMatch?.[0];
                if (!crq) return;
                const doCopy = (text) => {
                  setCopyMsg(text);
                  setTimeout(() => setCopyMsg(null), 2500);
                };
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(crq).then(() => doCopy(crq)).catch(() => {
                    const el = document.createElement('textarea');
                    el.value = crq; document.body.appendChild(el); el.select();
                    document.execCommand('copy'); document.body.removeChild(el);
                    doCopy(crq);
                  });
                } else {
                  const el = document.createElement('textarea');
                  el.value = crq; document.body.appendChild(el); el.select();
                  document.execCommand('copy'); document.body.removeChild(el);
                  doCopy(crq);
                }
              });
            }
          }}
          eventMouseEnter={handleEventMouseEnter}
          eventMouseLeave={handleEventMouseLeave}
          dayMaxEvents={false}
          height="auto"
          fixedWeekCount={false}
          firstDay={1}
          nowIndicator
          eventDisplay="block"
        />
      </Paper>

      {copyMsg && (
        <Box sx={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          bgcolor: 'grey.800', color: 'white', px: 2.5, py: 1, borderRadius: 2,
          fontSize: '0.8rem', fontWeight: 600, zIndex: 9999,
          boxShadow: 3, display: 'flex', alignItems: 'center', gap: 1,
        }}>
          ✓ Copied: {copyMsg}
        </Box>
      )}
      {tooltip?.entry && (
        <RichTooltip
          entry={tooltip.entry}
          mouseX={tooltip.mouseX}
          mouseY={tooltip.mouseY}
          featureColorMap={featureColorMap}
        />
      )}
    </>
  );
};

export default TrackerCalendar;